import clsx from 'clsx';
import { useRouter } from 'next/router';
import React from 'react';
import { createContainer } from 'unstated-next';

export type TabProps = {
  label: string;
  value: string;
};

export type TabContextProps = {
  initialValue: string;
  queryParamKey: string;
};

function useTabContext(initialState: TabContextProps | undefined) {
  if (!initialState) {
    throw new Error('useTabContext needs initialState, use the Tabs component');
  }
  const { initialValue, queryParamKey } = initialState;

  const { query } = useRouter();

  return {
    value: (query[queryParamKey] as string) || initialValue,
    queryParamKey,
  };
}

export const TabContext = createContainer(useTabContext);

export function Tab({ label, value }: TabProps) {
  const router = useRouter();
  const tabContext = TabContext.useContainer();
  const handleClick = async () => {
    await router.push(
      {
        query: {
          ...router.query,
          [tabContext.queryParamKey]: value,
        },
      },
      undefined,
      { shallow: true },
    );
  };

  const isActiveTab = tabContext.value === value;
  return (
    <button
      className={clsx('px-6 py-3 hover:bg-gray-100', {
        'text-brand border-b-2 border-brand -m-y-[2px]': isActiveTab,
      })}
      onClick={handleClick}
    >
      {label}
    </button>
  );
}

export type TabPanelProps = {
  children: React.ReactNode;
  value: string;
};

export function TabPanel({ children, value }: TabPanelProps) {
  const tabContext = TabContext.useContainer();
  if (tabContext.value !== value) {
    return null;
  }

  return <div>{children}</div>;
}

export function TabList({ children }: { children: React.ReactNode }) {
  return <div className="flex border-b border-gray-200">{children}</div>;
}

export type TabsProps = {
  children: React.ReactNode;
  initialValue: string;
  queryParamKey?: string;
};

export default function Tabs({
  children,
  initialValue,
  queryParamKey = 'tab',
}: TabsProps) {
  return (
    <TabContext.Provider
      initialState={{
        initialValue,
        queryParamKey,
      }}
    >
      {children}
    </TabContext.Provider>
  );
}
